/**
 * Deep Analysis Overlay
 * Shows a deliberate, step-by-step analysis loading state
 * that conveys expert-level review happening in the background.
 */

import React, { useState, useEffect } from 'react';
import { Loader2, CheckCircle, Circle, FileText, Search, Scale, AlertTriangle, FileCheck } from 'lucide-react';

// Analysis stages with timing
const ANALYSIS_STAGES = [
  { id: 'extracting', label: 'Extracting text and layout', icon: FileText, minDuration: 3000 },
  { id: 'mapping', label: 'Mapping lines and blocks', icon: Search, minDuration: 4000 },
  { id: 'dispute_rights', label: 'Checking dispute-rights language', icon: Scale, minDuration: 6000 },
  { id: 'validation', label: 'Validating creditor / amount / disclosure issues', icon: AlertTriangle, minDuration: 8000 },
  { id: 'comparing', label: 'Comparing overlapping theories', icon: Search, minDuration: 5000 },
  { id: 'precision', label: 'Building precise markup', icon: FileCheck, minDuration: 5000 },
  { id: 'finalizing', label: 'Finalizing review findings', icon: CheckCircle, minDuration: 4000 }
];

const DeepAnalysisOverlay = ({ 
  isVisible, 
  currentStage = 'extracting',
  progress = 0,
  onComplete
}) => {
  const [displayedStages, setDisplayedStages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Map backend stage to our stages
  useEffect(() => {
    const stageMap = {
      'starting': 0,
      'extracting': 0,
      'ocr': 0,
      'detecting_issues': 2,
      'ai_analysis': 3,
      'normalizing': 4,
      'building_annotations': 5,
      'finalizing': 6,
      'complete': 6
    };
    
    const newIndex = stageMap[currentStage] ?? Math.floor(progress / 15);
    if (newIndex > currentIndex) {
      setCurrentIndex(newIndex);
    }
  }, [currentStage, progress, currentIndex]);

  // Update displayed stages based on current index
  useEffect(() => {
    setDisplayedStages(ANALYSIS_STAGES.slice(0, currentIndex + 1));
  }, [currentIndex]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 bg-stone-900/90 backdrop-blur-sm flex items-center justify-center z-50" data-testid="deep-analysis-overlay">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Scale className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-stone-900">Deep Legal Analysis</h2>
          <p className="text-slate-600 mt-2 text-sm">
            Performing careful FDCPA compliance review...
          </p>
        </div>

        {/* Progress Stages */}
        <div className="space-y-3 mb-6">
          {ANALYSIS_STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            const isCompleted = idx < currentIndex;
            const isCurrent = idx === currentIndex;
            const isPending = idx > currentIndex;
            
            return (
              <div 
                key={stage.id}
                className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-500 ${
                  isCompleted ? 'bg-emerald-50 text-emerald-700' :
                  isCurrent ? 'bg-slate-100 text-stone-900' :
                  'bg-slate-50 text-slate-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  isCompleted ? 'bg-emerald-500 text-white' :
                  isCurrent ? 'bg-emerald-100' :
                  'bg-slate-200'
                }`}>
                  {isCompleted ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : isCurrent ? (
                    <Loader2 className="w-5 h-5 text-emerald-600 animate-spin" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                </div>
                <span className={`text-sm font-medium ${isPending ? 'opacity-50' : ''}`}>
                  {stage.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-slate-600 mb-2">
            <span>Overall Progress</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Quality Message */}
        <div className="text-center text-xs text-slate-500 border-t border-slate-200 pt-4">
          <p>Taking time for accuracy. Quality over speed.</p>
        </div>
      </div>
    </div>
  );
};

export default DeepAnalysisOverlay;
